import { DeviceOverviewStatus, DeviceType } from '../types';
import * as backend from './backend-types';
import {
  DEFAULT_PORTAL_PATIENT_BARCODE_FORMAT,
  DEFAULT_PORTAL_PATIENT_ID_FORMAT,
  HEALTHDOT_CODE_PREFIX,
  HEALTHDOT_CODE_PREFIX_REGEX,
  HEALTHDOT_SERIAL_REGEX,
  VIQTOR_UDI_REGEX,
} from './constants';

export type ScannedDeviceType = DeviceType;

export type ScannedDeviceInfo = {
  deviceType: ScannedDeviceType;
  serialNumber: string;
  format: 'HD5' | 'VIQTOR_UDI' | 'RAW';
  /**
   * Whether an identifier of the recognised format could actually be read. A HealthDot
   * whose prefix matches but whose serial does not is `format: 'HD5'` with `valid: false`
   * -- recognised, but unusable. Never create a device in the backend from one of those:
   * it would register a device nobody can pair afterwards.
   */
  valid: boolean;
};

export function decodePortalAuthorizationCode(code: string): string {
  if (!code) return '';
  try {
    const normalized = decodeURIComponent(code);
    const decoded = atob(normalized);
    const bytes = Uint8Array.from(decoded, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    try {
      return atob(decodeURIComponent(code));
    } catch {
      return code;
    }
  }
}

export function encodePortalAuthorizationCode(value: string): string {
  const normalized = (value || '').trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

export function toLanguageTag(locale?: string): string | undefined {
  if (!locale) return undefined;
  const normalized = locale.replace('_', '-').toLowerCase();
  const [language] = normalized.split('-');
  return language || undefined;
}

export function getPortalUsername(user: backend.PortalUserResponse): string {
  const firstName = user.personalData?.firstName?.trim();
  const lastName = user.personalData?.lastName?.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || user.email || 'Unknown user';
}

export function getPortalOrganizationName(user: backend.PortalUserResponse): string | undefined {
  return user.healthCareOrganization?.name;
}

export function getPortalOrganizationLanguage(
  user: backend.PortalUserResponse,
): string | undefined {
  return toLanguageTag(user.healthCareOrganization?.locale);
}

export function getPortalOrganizationId(user: backend.PortalUserResponse): string {
  return user.healthCareOrganization?.id || '';
}

export function getPortalPatientIdFormat(): string {
  return DEFAULT_PORTAL_PATIENT_ID_FORMAT;
}

export function getPortalPatientBarcodeFormat(): string {
  return DEFAULT_PORTAL_PATIENT_BARCODE_FORMAT;
}

export function parseScannedDeviceCode(scannedCode: string): ScannedDeviceInfo {
  const code = scannedCode.trim();

  // The prefix identifies the family, the serial pattern decides readability -- see the
  // note on the HealthDot patterns in constants.ts.
  if (HEALTHDOT_CODE_PREFIX_REGEX.test(code)) {
    const serialNumber = code.substring(HEALTHDOT_CODE_PREFIX.length);
    return {
      deviceType: DeviceType.Healthdot,
      serialNumber,
      format: 'HD5',
      valid: HEALTHDOT_SERIAL_REGEX.test(serialNumber),
    };
  }

  const viQtorMatch = code.match(VIQTOR_UDI_REGEX);
  if (viQtorMatch) {
    return {
      deviceType: DeviceType.ViQtor,
      serialNumber: viQtorMatch[1],
      format: 'VIQTOR_UDI',
      valid: true,
    };
  }

  // Not a recognised device code. A bare viQtor serial lands here too: the wizard supplies
  // the device type for that path, so `valid` is only ever consulted for HealthDots.
  return {
    deviceType: DeviceType.Unknown,
    serialNumber: code,
    format: 'RAW',
    valid: false,
  };
}

export function normalizeScannedDeviceCode(scannedCode: string): string {
  return parseScannedDeviceCode(scannedCode).serialNumber;
}

const DEVICE_OVERVIEW_STATUSES: readonly DeviceOverviewStatus[] = [
  'DISCONNECTED',
  'CHARGING',
  'NOT_WORN',
  'BATTERY_CRITICAL',
  'BATTERY_LOW',
  'OK',
  'UNKNOWN',
];

function isDeviceOverviewStatus(status: string): status is DeviceOverviewStatus {
  return DEVICE_OVERVIEW_STATUSES.includes(status as DeviceOverviewStatus);
}

export function toDeviceOverviewStatus(
  status?: backend.PortalDeviceStatus | string,
): DeviceOverviewStatus {
  if (!status) return 'UNKNOWN';
  const normalized = status.trim().toUpperCase();
  return isDeviceOverviewStatus(normalized) ? normalized : 'UNKNOWN';
}
